/**
 * Firm-wide report branding - custom text OR a logo image, printed in the top
 * left of page 1 of every generated report.
 *
 * Read-through cache: one GET serves the Manual widget and every report window
 * opened afterwards, so a print doesn't wait on a round trip. The cache is
 * in-memory module state (never persisted) and is dropped on every mutation and
 * on logout, so a second user on the same tab never inherits the previous firm's
 * mark. Mirrors lib/variableRateBonds.js.
 */
import { API_ENDPOINTS } from '../../../config/api';

let cache = null; // resolved { text, logo_data_url, updated_at }
let inflight = null; // dedupes concurrent callers
let epoch = 0; // bumped on clear so a stale in-flight response can't repopulate the cache

/**
 * Resolve the firm's branding, reusing the cache (or an in-flight request).
 * Pass `{ force: true }` to bypass the cache and refetch. `fetcher` is
 * AuthContext's fetchWithAuth (401 -> refresh -> retry).
 */
export function getFirmBranding(fetcher, { force = false } = {}) {
  if (!force && cache) return Promise.resolve(cache);
  if (!force && inflight) return inflight;
  const started = epoch;
  const request = fetcher(API_ENDPOINTS.manual.firmBranding)
    .then((res) => {
      if (!res.ok) throw new Error(`firm-branding ${res.status}`);
      return res.json();
    })
    .then((data) => {
      if (epoch === started) {
        cache = data;
        inflight = null;
      }
      return data;
    })
    .catch((err) => {
      if (epoch === started) inflight = null;
      throw err;
    });
  inflight = request;
  return request;
}

/** Drop the cached branding (call on logout / token clear). */
export function clearFirmBrandingCache() {
  cache = null;
  inflight = null;
  epoch += 1;
}

// --- Mutations -------------------------------------------------------------
//
// Each one clears the cache on success, so the next read is server truth rather
// than the pre-mutation mark. Text and logo are mutually exclusive server-side:
// setting one clears the other.

async function mutate(fetcher, options) {
  const res = await fetcher(API_ENDPOINTS.manual.firmBranding, options);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || `firm-branding ${res.status}`);
  }
  const data = await res.json();
  clearFirmBrandingCache();
  return data;
}

const putJson = (body) => ({
  method: 'PUT',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

/** PUT custom branding text; resolves the updated branding. */
export function saveFirmBrandingText(fetcher, text) {
  return mutate(fetcher, putJson({ text }));
}

/** PUT a logo image as base64 (no `data:*;base64,` prefix). */
export function saveFirmBrandingLogo(fetcher, logoB64) {
  return mutate(fetcher, putJson({ logo_b64: logoB64 }));
}

/** DELETE the branding (clears both text and logo). */
export function clearFirmBranding(fetcher) {
  return mutate(fetcher, { method: 'DELETE' });
}
